import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams, useSearchParams } from "react-router";
import { Button } from "@/components/design/Button";
import { Disclosure } from "@/components/design/Disclosure";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { MultiSelect } from "@/components/design/MultiSelect";
import { Section } from "@/components/design/Section";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Switch } from "@/components/design/Switch";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Tooltip } from "@/components/common/Tooltip";
import {
  EMPTY_TITLE_FILTERS,
  EMPTY_VALUATION_MANUAL,
  createLabSessionResult,
  deleteLabSessionResult,
  fetchFilterOptions,
  fetchLabSession,
  fetchLabSessionResult,
  fetchLabSessionResults,
  fetchTitleDetail,
  searchKeywords,
  updateLabSession,
  TobisNotConfiguredError,
  type FilterOptions,
  type KeywordMatch,
  type TobisPersonRef,
  type TobisTitle,
  type TobisCastMember,
  type TobisLabSessionResultDetail,
  type TobisLabSessionResultSummary,
  type TobisTitleDetail,
  type TobisTitleFilters,
  type TobisValuationManualInput,
} from "@/lib/tobis";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAgentStore } from "@/stores/agent-store";
import { TobisPageShell } from "./TobisPageShell";
import { isTobisAiEnabled } from "@/lib/tobis-mode";
import { useAutoStartOnce } from "@/lib/use-auto-start-once";
import { SHOW_PITCH_AI_GENERATE, SHOW_TOBIS_ALGORITHM_TOGGLE } from "./lab-visibility";
import { useKeywordSuggestions } from "./use-keyword-suggestions";
import { TobisPersonPicker } from "./TobisPersonPicker";
import {
  TobisValuationPanel,
  type ComparablesAssessment,
  type ValuationCastMember,
  type ValuationComparableInput,
} from "./TobisValuationPanel";
import { buildLabAssistantContext } from "./tobis-lab-assistant-context";
import {
  findComparables,
  type FindComparablesInput,
  type FindComparablesProgressEvent,
} from "./tobis-lab-find-comparables";
import { resolveTitles } from "./tobis-lab-resolve-titles";
import { loadComparables, type ResolvedComparable } from "./tobis-lab-load-comparables";
import { readFileAsBase64, summarizeScript } from "./tobis-lab-script-summary";
import { extractCandidateTerms, nameTokens } from "./tobis-lab-terms";
import { resolvePersons } from "./tobis-lab-resolve-persons";
import { LAB_STEPS, nextStepId, parseStepId, prevStepId, stepIndex, type LabStepId } from "./tobis-lab-types";

// Der Labor-Editor zu EINER Sitzung (app.lab_session, V0034 im BFF) - der
// bisherige Inhalt von TobisLabPage, jetzt an :sessionId gebunden statt an
// reinen Browser-State. TobisLabPage selbst ist die vorgelagerte Landing
// (Liste + "Neu"), siehe dort.
//
// Geladen wird einmal beim Mount (fetchLabSession), danach debounced
// autogespeichert bei jeder Aenderung an Pitch/Kriterien - kein Speichern-
// Knopf, im selben Rhythmus wie vorher.
//
// Architektur-Wende 2026-08-06 (Marvin): Auswahl der Vergleichstitel und
// Potenzial-Einschaetzung sind fachlich EIN Schritt, kein Pool-Ranking plus
// separate Begruendung/Bewertung mehr. Ein einziger Aufruf
// (TobisPitchController.findComparables im agent-service) holt sich selbst
// die volle Filmografie (inkl. Erloes/Budget) zu Regie/Drehbuchautor:in/Cast,
// rangiert UNABHAENGIG davon einen Themen-Pool nach Genre/Sprache/Keywords,
// beruecksichtigt namentlich genannte Titel (pinnedTitleIds) und liefert in
// einer Antwort sowohl die ausgewaehlten Vergleichstitel mit Begruendung als
// auch die Potenzial-Einschaetzung. Das behebt den Tom-Hanks-Vorfall durch
// Konstruktion: Personen-Kontext und Themen-Rangierung sind zwei getrennte,
// nicht konkurrierende Quellen statt einer vermischten Rangierung.
//
// Weiterhin BEWUSSTE Abweichungen von Match, weil es dafuer keine
// Entsprechung gibt: kein editierbarer Name im Editor selbst (die Landing
// zeigt pitchTitle als Anzeigename), kein Agentic-AI-Schalter (reine Deko in
// Match, "noch nicht angebunden"), kein Solver/Powers/Zugriffsliste. Der
// Kriterien-Schritt hat dagegen eine eigene Dokumenten-Dropzone (Skript-PDF
// -> TobisScriptSummaryController) - die Zusammenfassung fliesst wirklich in
// findComparables ein.

type Phase = "loading" | "ready" | "notFound" | "notConfigured" | "error";

/** Erste paar echten Schauspiel-Credits (characterName gesetzt schliesst reine Crew-Zeilen
 *  aus, die bei TMDB-Titeln ohne cast_order ans Ende der ohnehin nach Billing sortierten
 *  Liste fallen, siehe titleCast im BFF) - "3-5" auf Marvins Zuruf, hier 5 als Obergrenze.
 *  personPhotoUrl ist oft null (v.a. TVmaze-Personen) - TobisValuationPanel zeigt dann einen
 *  Initialen-Platzhalter statt eines Fotos. */
function topCast(cast: readonly TobisCastMember[]): ValuationCastMember[] {
  return cast
    .filter((c) => c.characterName !== null && c.personName !== null)
    .slice(0, 5)
    .map((c) => ({ name: c.personName!, photoUrl: c.personPhotoUrl }));
}

// Dieselben Job-Bezeichnungen wie das Drehbuch-Matching (model.script, siehe
// V0035/V0037/V0043/V0079 - c.jobs && ARRAY['Screenplay','Writer','Story',
// 'Screenstory','Adaptation']), hier nur auf den schon geladenen Cast eines
// Vergleichstitels angewandt statt eine eigene Sicht abzufragen.
const WRITING_JOBS = ["Screenplay", "Writer", "Story", "Screenstory", "Adaptation"];

function creditNames(cast: readonly TobisCastMember[], jobs: readonly string[]): string[] {
  const names: string[] = [];
  for (const c of cast) {
    if (c.personName && c.jobs.some((j) => jobs.includes(j)) && !names.includes(c.personName)) {
      names.push(c.personName);
    }
  }
  return names;
}

export function TobisLabSessionPage() {
  const intl = useIntl();
  const { sessionId } = useParams<{ sessionId: string }>();
  const ask = useAgentStore((s) => s.ask);
  // Drehbuch-Zusammenfassung und "Assistent fragen" laufen ueber agent-service.
  const labAi = isTobisAiEnabled();
  const [params, setParams] = useSearchParams();
  const stepId = parseStepId(params.get("step"));
  const setStepId = useCallback(
    (next: LabStepId) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set("step", next);
      setParams(nextParams, { replace: true });
    },
    [params, setParams],
  );

  const [pitchTitle, setPitchTitle] = useState("");
  const [pitchShort, setPitchShort] = useState("");
  const [pitchDescription, setPitchDescription] = useState("");
  // Gemeinsam mit handleFindComparables genutzt - frueh deklariert, damit
  // jede spaetere Funktion in dieser Komponente ihn referenzieren kann.
  const pitchText = [pitchTitle, pitchShort, pitchDescription].map((s) => s.trim()).filter(Boolean).join(". ");
  const [pitchHints, setPitchHints] = useState("");
  const [keywordMatches, setKeywordMatches] = useState<KeywordMatch[]>([]);
  const [generating, setGenerating] = useState(false);
  const [keywordSearchEmpty, setKeywordSearchEmpty] = useState(false);
  // Freitext fuer das manuelle Hinzufuegen eines einzelnen Keywords, getrennt
  // von pitchHints/pitchDescription: "Mit KI generieren" ersetzt die ganze
  // Chip-Reihe aus dem Pitch-Text, dieses Feld ergaenzt gezielt ein einzelnes
  // Wort ohne die vorhandene Auswahl anzutasten.
  const [keywordInput, setKeywordInput] = useState("");
  // Vorschlaege zum Tippen. Vorher fuegte Enter ALLE Treffer eines Begriffs auf
  // einmal ein: "drama" brachte 30 Chips von "period drama" bis "drama class",
  // die man einzeln wieder entfernen musste (Marvin, 2026-08-19). Die
  // Titel-Filterleiste konnte das laengst besser; beide nutzen jetzt denselben
  // Hook.
  const keywordSuggestions = useKeywordSuggestions(keywordInput);
  // Vergleichsfilme, die im Pitch-Text namentlich genannt wurden (siehe
  // resolveTitles) - immer an findComparables mitgegeben (pinnedTitleIds),
  // unabhaengig vom Themen-Pool. Nur die Ids werden persistiert
  // (criteria.pinnedTitleIds), volle Objekte hydriert sich hydratePinnedTitles
  // beim Laden neu.
  const [pinnedTitles, setPinnedTitles] = useState<TobisTitle[]>([]);
  const [findingComparables, setFindingComparables] = useState(false);
  // Echte Fortschritts-Anzeige waehrend findComparables laeuft (Marvin,
  // 2026-08-07) - eine Zeile pro tatsaechlichem Teilaufruf (Filmografie je
  // Person, Themen-Pool, gepinnte Titel, Modell-Aufruf), nicht ein simulierter
  // Timer. Reihenfolge = Ankunftsreihenfolge der stage_started-Events, die bei
  // den parallel laufenden Filmografie-/Themen-Pool-Abfragen echt verschraenkt
  // eintreffen koennen.
  const [progressStages, setProgressStages] = useState<{ stage: string; label: string; done: boolean }[]>([]);
  // Fehlschlaege der Vergleichssuche waren bisher unsichtbar: der catch unten
  // hat nur geloggt, im UI blieb der Klick wirkungslos - "erneut suchen tut
  // nichts" war deshalb nicht von "Suche laeuft ins Leere" zu unterscheiden
  // (Marvin, 2026-08-14). Die Meldung geht ueber userErrorMessage, nie roh
  // (ADR-0010).
  const [findError, setFindError] = useState<string | null>(null);
  // Verlauf statt ein einzelnes ueberschriebenes Feld (Marvin, 2026-08-07:
  // "wenn ich neue Runs mache, ueberschreibe ich ja den gespeicherten
  // Zustand" / "ich brauche wie MatchResult eine Uebersicht") - jeder
  // findComparables-Lauf legt eine eigene Zeile an (app.lab_session_result,
  // V0070), `openResultId` ist, welche davon gerade angezeigt wird (wie
  // Matchs `?result=<id>`).
  const [resultsList, setResultsList] = useState<TobisLabSessionResultSummary[]>([]);
  const [resultsListLoaded, setResultsListLoaded] = useState(false);
  const [openResultId, setOpenResultId] = useState<string | null>(null);
  const [loadingResultDetail, setLoadingResultDetail] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<ComparablesAssessment | null>(null);
  const [resolved, setResolved] = useState<ResolvedComparable[]>([]);
  const [failedComparableIds, setFailedComparableIds] = useState<string[]>([]);
  // Der Prompt, den findComparables tatsaechlich an das Modell geschickt hat
  // (Filmografien, Themen-Pool, gepinnte Titel) - nur fuer die "Kontext
  // anzeigen"-Ausklappe unten, damit nachvollziehbar bleibt, was der KI
  // tatsaechlich vorlag (Marvin, 2026-08-07).
  const [context, setContext] = useState<string | null>(null);
  const [compInclude, setCompInclude] = useState<Record<string, boolean>>({});
  const [valuationManual, setValuationManual] = useState<TobisValuationManualInput>(EMPTY_VALUATION_MANUAL);

  const [director, setDirector] = useState<TobisPersonRef | null>(null);
  const [writer, setWriter] = useState<TobisPersonRef | null>(null);
  const [cast, setCast] = useState<TobisPersonRef[]>([]);
  const [scriptSummary, setScriptSummary] = useState<string | null>(null);
  const [uploadingScript, setUploadingScript] = useState(false);
  // Optionale eigene Schaetzung (Marvin, 2026-08-07) - USD wie TobisTitle.budgetUsd/revenueUsd,
  // damit sie direkt gegen die Vergleichstitel-Zahlen unten in derselben Einheit lesbar ist.
  // Kein Pflichtfeld, deshalb nicht in missingRequired.
  const [expectedBudgetUsd, setExpectedBudgetUsd] = useState("");
  const [expectedRevenueUsd, setExpectedRevenueUsd] = useState("");
  // Reine Anzeigesache (Marvins Entscheidung 2026-08-07, gegen meine Warnung):
  // die Zahl selbst bleibt unveraendert und geht als expectedBudgetUsd/
  // expectedRevenueUsd unkonvertiert raus, verglichen wird server-/modellseitig
  // direkt gegen echte USD-Zahlen. Bei EUR-Eingabe ist der Vergleich damit um
  // den Wechselkurs daneben - kein Umrechnungsfehler, sondern Absicht.
  const [expectedCurrency, setExpectedCurrency] = useState<"EUR" | "USD">("EUR");
  // "TOBIS Algorithmus"-Modus (Marvin, 2026-08-07): erzwingt statt der freien
  // yearFrom/yearTo ein 12-Jahre-Fenster, filtert den Themen-Pool auf DE-Kinostart
  // ODER Streaming-Original und laesst 5-15 statt bis zu 8 Vergleichsfilme zu -
  // siehe FindComparablesInput.tobisAlgorithm/TobisPitchController im agent-service.
  const [tobisAlgorithm, setTobisAlgorithm] = useState(false);
  // "Nur diese Vergleichsfilme" (2026-10-06): die angepinnten Titel sind der ganze
  // Vergleichssatz - siehe FindComparablesInput.fixedComparables. Wirksam nur, solange
  // ueberhaupt Titel angepinnt sind; der gespeicherte Schalter bleibt davon unberuehrt.
  const [fixedComparables, setFixedComparables] = useState(false);
  const fixedActive = fixedComparables && pinnedTitles.length > 0;

  const [filters, setFilters] = useState<TobisTitleFilters>(EMPTY_TITLE_FILTERS);
  const [options, setOptions] = useState<FilterOptions | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");

  // Wird erst nach dem ersten erfolgreichen Laden true - vorher wuerde jeder
  // Autosave-Effekt den frisch geladenen Serverstand mit den React-
  // Anfangswerten (EMPTY_TITLE_FILTERS etc.) ueberschreiben, noch bevor er
  // sichtbar war.
  const hydrated = useRef(false);

  const keywordIds = keywordMatches.map((k) => k.keywordId);

  // Setzt assessment/context/resolved aus einem bereits geholten Result-Blob
  // (egal ob aus fetchLabSessionResult oder direkt aus createLabSessionResult
  // - beide liefern dieselbe Form). Vor der Session-Load-Effekt deklariert,
  // die sie beim Start aufruft - sonst sieht eslint (react-hooks) eine
  // Funktionsdeklaration in einer Komponente als "vor der Deklaration
  // benutzt", wenn ein Effekt sie transitiv aufruft.
  async function hydrateResultDetail(detail: TobisLabSessionResultDetail) {
    setAssessment({
      positive: detail.positive,
      negative: detail.negative,
      fazit: detail.fazit,
      suitability: detail.suitability,
    });
    setContext(detail.context);
    setFailedComparableIds([]);
    const details = await loadComparables(detail.selected);
    setResolved(details.resolved);
    setFailedComparableIds(details.failedIds);
  }

  // Oeffnet einen gespeicherten Lauf aus der "Fruehere Ergebnisse"-Liste (Klick
  // auf einen Eintrag) - im Unterschied zu handleFindComparables/einem frisch
  // erzeugten Result, das seinen Blob schon hat und hydrateResultDetail direkt
  // aufruft, ohne hier noch einmal nachzufragen.
  async function openResult(id: string) {
    if (!sessionId || loadingResultDetail) return;
    setOpenResultId(id);
    const next = new URLSearchParams(params);
    next.set("result", id);
    setParams(next, { replace: true });
    setLoadingResultDetail(true);
    try {
      await hydrateResultDetail(await fetchLabSessionResult(sessionId, id));
    } catch {
      logApplicationFailure("tobis.lab.session.resultDetail");
    } finally {
      setLoadingResultDetail(false);
    }
  }

  async function handleDeleteResult(id: string) {
    if (!sessionId) return;
    try {
      await deleteLabSessionResult(sessionId, id);
      const remaining = resultsList.filter((r) => r.id !== id);
      setResultsList(remaining);
      if (openResultId === id) {
        if (remaining.length > 0) {
          void openResult(remaining[0]!.id);
        } else {
          setOpenResultId(null);
          setResolved([]);
          setFailedComparableIds([]);
          setAssessment(null);
          setContext(null);
          const next = new URLSearchParams(params);
          next.delete("result");
          setParams(next, { replace: true });
        }
      }
    } catch {
      logApplicationFailure("tobis.lab.session.deleteResult");
    } finally {
      setDeleteConfirmId(null);
    }
  }

  // Einmal pro Sitzung laden. sessionId aendert sich praktisch nie waehrend
  // eines Editor-Aufenthalts (kein In-Place-Wechsel der Sitzung), steht aber
  // in den deps, damit ein direkter URL-Wechsel (Browser-Zurueck zwischen
  // zwei Sitzungen) korrekt neu laedt. openResult/params bewusst NICHT in den
  // deps: params aendert sich bei jedem Schritt-Wechsel (?step=...), das
  // wuerde die Sitzung unnoetig neu laden - dieser Effekt liest params/ruft
  // openResult nur EINMAL beim initialen Laden auf, um einen Deep-Link
  // (?result=<id>) zu respektieren, nicht um darauf zu reagieren.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    hydrated.current = false;
    setPhase("loading");
    void fetchLabSession(sessionId)
      .then((session) => {
        if (cancelled) return;
        if (!session) {
          setPhase("notFound");
          return;
        }
        setPitchTitle(session.pitchTitle);
        setPitchShort(session.pitchShort);
        setPitchDescription(session.pitchDescription);
        setPitchHints(session.pitchHints);
        setFilters(session.criteria.filters ?? EMPTY_TITLE_FILTERS);
        setKeywordMatches(session.criteria.keywordMatches ?? []);
        setDirector(session.criteria.director ?? null);
        setWriter(session.criteria.writer ?? null);
        setCast(session.criteria.cast ?? []);
        setScriptSummary(session.criteria.scriptSummary ?? null);
        setExpectedBudgetUsd(session.criteria.expectedBudgetUsd ?? "");
        setExpectedRevenueUsd(session.criteria.expectedRevenueUsd ?? "");
        setTobisAlgorithm(session.criteria.tobisAlgorithm ?? false);
        setFixedComparables(session.criteria.fixedComparables ?? false);
        setCompInclude(session.criteria.compInclude ?? {});
        setValuationManual(session.criteria.valuationManual ?? EMPTY_VALUATION_MANUAL);
        const pinnedIds = session.criteria.pinnedTitleIds ?? [];
        hydrated.current = true;
        setPhase("ready");
        // Nur die Ids sind persistiert - volle Objekte einzeln nachladen (klein,
        // hoechstens ein paar Titel). Nicht awaited: darf den restlichen Editor
        // nicht verzoegern, ein Fehlschlag hier ist kein Ladefehler der Sitzung.
        if (pinnedIds.length > 0) {
          void Promise.all(pinnedIds.map((id) => fetchTitleDetail(id).catch(() => null)))
            .then((details) => setPinnedTitles(details.filter((d): d is TobisTitleDetail => d !== null).map((d) => d.title)))
            .catch(() => logApplicationFailure("tobis.lab.session.pinnedTitles"));
        }
        // "Fruehere Ergebnisse": alle gespeicherten Laeufe dieser Sitzung.
        // Default beim Oeffnen ist der neueste (resultsList ist bereits nach
        // createdAt DESC sortiert, siehe listLabSessionResults) - ausser die
        // URL nennt schon einen bestimmten (Browser-Zurueck/Lesezeichen,
        // gleiches Muster wie Matchs `?result=<id>`).
        const urlResultId = params.get("result");
        void fetchLabSessionResults(sessionId)
          .then((list) => {
            setResultsList(list);
            setResultsListLoaded(true);
            const initial = urlResultId && list.some((r) => r.id === urlResultId) ? urlResultId : list[0]?.id;
            if (initial) void openResult(initial);
          })
          .catch(() => logApplicationFailure("tobis.lab.session.resultsList"));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof TobisNotConfiguredError) {
          setPhase("notConfigured");
          return;
        }
        logApplicationFailure("tobis.lab.session.load");
        setPhase("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    void fetchFilterOptions()
      .then(setOptions)
      .catch(() => setOptions(null));
  }, []);

  // Entprellt, damit Tippen im Suchfeld nicht pro Zeichen eine Abfrage macht.
  // Speichert im selben Takt ALLE Kriterien (auch director/writer/cast) - das
  // BFF ersetzt die gesamte criteria-jsonb-Spalte pro PATCH (kein Merge, siehe
  // lab-sessions.ts), ein zweiter, separater Speicher-Pfad fuer nur einen Teil
  // der Kriterien wuerde die uebrigen Felder beim naechsten Tick ueberschreiben.
  useEffect(() => {
    if (!sessionId) return;
    const timer = window.setTimeout(() => {
      if (hydrated.current) {
        void updateLabSession(sessionId, {
          criteria: {
            filters,
            keywordMatches,
            director,
            writer,
            cast,
            scriptSummary,
            expectedBudgetUsd: expectedBudgetUsd.trim() || null,
            expectedRevenueUsd: expectedRevenueUsd.trim() || null,
            compInclude,
            valuationManual,
            pinnedTitleIds: pinnedTitles.map((t) => t.id),
            tobisAlgorithm,
            fixedComparables,
          },
        }).catch(() => logApplicationFailure("tobis.lab.session.autosaveCriteria"));
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [
    filters,
    keywordMatches,
    director,
    writer,
    cast,
    scriptSummary,
    expectedBudgetUsd,
    expectedRevenueUsd,
    compInclude,
    valuationManual,
    pinnedTitles,
    tobisAlgorithm,
    fixedComparables,
    sessionId,
  ]);

  // Pitch-Felder eigenes, etwas laengeres Debounce (Freitext-Tippen ist
  // schneller als ein Filterklick) - getrennt vom Kriterien-Effekt oben, weil
  // der zusaetzlich einen Reset ausloest und das bei jedem Tastenanschlag im
  // Pitch-Text unnoetig waere, wenn es nur um den Serverstand ginge.
  useEffect(() => {
    if (!sessionId || !hydrated.current) return;
    const timer = window.setTimeout(() => {
      void updateLabSession(sessionId, { pitchTitle, pitchShort, pitchDescription, pitchHints }).catch(() =>
        logApplicationFailure("tobis.lab.session.autosavePitch"),
      );
    }, 600);
    return () => window.clearTimeout(timer);
  }, [pitchTitle, pitchShort, pitchDescription, pitchHints, sessionId]);

  useAssistantContext(
    phase === "ready"
      ? buildLabAssistantContext(
          {
            title: pitchTitle,
            shortDescription: pitchShort,
            description: pitchDescription,
            tone: pitchHints,
            director,
            writer,
            cast,
            scriptSummary,
          },
          filters,
          keywordMatches.map((k) => k.keyword),
          resolved.map((r) => r.title),
          false,
        )
      : null,
  );

  const canGenerate = pitchDescription.trim().length > 0 || pitchHints.trim().length > 0;

  // Mindestangaben (siehe Session-Diskussion): Synopsis, Genre. Sprache war
  // hier ebenfalls Pflicht, ist es auf Marvins Zuruf nicht mehr - die
  // Vergleichbarkeit haengt fachlich nicht an der Sprache, das Filterfeld
  // bleibt optional nutzbar. Alles andere im Pitch-Schritt (Arbeitstitel,
  // Regie, Cast, Tonalitaet) bleibt ebenfalls optional. Gate fuer den Wechsel
  // zu Result und fuer "Vergleichstitel finden".
  const missingRequired = [
    pitchDescription.trim().length === 0
      ? intl.formatMessage({ id: "tobis.lab.pitchDescriptionLabel", defaultMessage: "Pitch" })
      : null,
    filters.genres.length === 0
      ? intl.formatMessage({ id: "tobis.filter.genres", defaultMessage: "Genres" })
      : null,
  ].filter((v): v is string => v !== null);
  const pitchComplete = missingRequired.length === 0;

  async function handleGenerate() {
    if (!canGenerate || generating) return;
    setGenerating(true);
    setKeywordSearchEmpty(false);
    try {
      const text = `${pitchDescription} ${pitchHints}`;

      // Personennamen sind keine Themen (Marvin, 2026-08-19): "Emma Stone" im
      // Pitch brachte "stone", "stone age" und "philosopher's stone". Zwei
      // Quellen fuer die Namen, weil beide Faelle vorkommen - im Formular
      // gewaehlte Personen kennen wir sofort und ohne Kosten, im Text genannte
      // nur ueber resolvePersons (dieselbe Aufloesung, die das Personenfeld
      // benutzt).
      const namenAusFormular = [director, writer, ...cast].flatMap((p) =>
        nameTokens(p?.personName),
      );
      const personen = await resolvePersons(text).catch(() => {
        // Ein Fehlschlag hier darf den Knopf nicht anhalten: die Namen aus dem
        // Formular decken den haeufigsten Fall schon ab.
        logApplicationFailure("tobis.lab.generate.persons");
        return [];
      });
      const ausgeschlossen = [
        ...namenAusFormular,
        ...personen.flatMap((p) => nameTokens(p.name)),
      ];
      const terms = extractCandidateTerms(text, ausgeschlossen);
      // Zwei getrennte Signale aus demselben Text: Themen-Tags (Substring-
      // Abgleich, kein KI-Aufruf) und namentlich genannte Vergleichsfilme
      // (echter KI-Aufruf, ResolveTitles). Ein "X trifft Y"-Vergleichsfilm ist
      // kein Thema, die Wort-fuer-Wort-Suche findet ihn nie - siehe Session-
      // Diskussion 2026-08-06 ("Prince of Persia").
      // allSettled und nicht all: die beiden Signale sind unabhaengig, aber
      // Promise.all koppelte sie. Der Keyword-Abgleich laeuft rein in der
      // Datenbank, resolveTitles ist ein echter KI-Aufruf ueber den
      // agent-service - fiel der aus (etwa der 502 vom 2026-08-19, als ein
      // Container ohne den Tobis-Controller lief), landete der ganze Block im
      // catch und die schon gefundenen Keywords wurden verworfen. Der Knopf sah
      // dann aus, als haette er nichts gefunden.
      const [keywordResult, titleResult] = await Promise.allSettled([
        terms.length ? searchKeywords(terms) : Promise.resolve([]),
        resolveTitles(text),
      ]);
      const matches = keywordResult.status === "fulfilled" ? keywordResult.value : [];
      if (keywordResult.status === "rejected") logApplicationFailure("tobis.lab.generate.keywords");
      if (titleResult.status === "rejected") logApplicationFailure("tobis.lab.generate.titles");

      // Ergaenzen statt ersetzen (Marvin, 2026-08-19): von Hand gewaehlte
      // Keywords sind Arbeit, die ein zweiter Klick auf "Mit KI generieren"
      // nicht wegwerfen darf. Dieselbe Regel gilt schon fuer die erkannten
      // Vergleichsfilme unten und fuer die Vorschlagsliste am Eingabefeld.
      // Die Zahl der neuen Treffer VOR dem Setzen bestimmen, aus dem aktuellen
      // Zustand: ein Updater laeuft erst beim naechsten Rendern, eine Variable
      // aus seinem Rumpf waere in der Zeile darunter noch unveraendert (und
      // unter StrictMode zusaetzlich doppelt beschrieben).
      const bekannt = new Set(keywordMatches.map((k) => k.keywordId));
      const neueKeywords = matches.filter((m) => !bekannt.has(m.keywordId)).length;
      setKeywordMatches((prev) => {
        const seen = new Set(prev.map((k) => k.keywordId));
        const frisch = matches.filter((m) => !seen.has(m.keywordId));
        return frisch.length ? [...prev, ...frisch] : prev;
      });
      // Ohne diesen Hinweis sieht ein Null-Treffer-Lauf identisch aus wie "nie
      // geklickt": die Chip-Reihe unten rendert bei leerem Array einfach
      // nichts. Passiert leicht bei ungewoehnlichem Wortschatz (Eigennamen,
      // erfundene Begriffe) - die Wort-fuer-Wort-Suche trifft dann keinen
      // TMDB-Tag. Gemessen wird an den NEU hinzugekommenen: ein Lauf, der nur
      // schon Gewaehltes findet, hat fachlich auch nichts gebracht.
      setKeywordSearchEmpty(neueKeywords === 0);
      const titleRefs = titleResult.status === "fulfilled" ? titleResult.value : [];
      if (titleRefs.length > 0) {
        const details = await Promise.all(titleRefs.map((r) => fetchTitleDetail(r.id).catch(() => null)));
        setPinnedTitles((prev) => {
          const seen = new Set(prev.map((t) => t.id));
          const added = details.filter((d): d is TobisTitleDetail => d !== null && !seen.has(d.title.id));
          return [...prev, ...added.map((d) => d.title)];
        });
      }
    } catch {
      logApplicationFailure("tobis.lab.generate");
    } finally {
      setGenerating(false);
    }
  }

  function removePinnedTitle(id: string) {
    setPinnedTitles((prev) => prev.filter((t) => t.id !== id));
  }

  function removeKeyword(keywordId: string) {
    setKeywordMatches((prev) => prev.filter((k) => k.keywordId !== keywordId));
  }

  /** Ein einzelnes Keyword aus der Vorschlagsliste uebernehmen. */
  function addKeyword(match: KeywordMatch) {
    setKeywordSearchEmpty(false);
    setKeywordInput("");
    setKeywordMatches((prev) =>
      prev.some((k) => k.keywordId === match.keywordId) ? prev : [...prev, match],
    );
  }

  // Enter uebernimmt EIN Keyword, nicht alle Treffer: bevorzugt den genau
  // getippten Begriff, sonst den ersten Vorschlag (die Route sortiert nach
  // Titelzahl, der erste ist also der am haeufigsten belegte). Wer etwas
  // anderes will, klickt es in der Liste an.
  function addKeywordTerm() {
    const term = keywordInput.trim().toLowerCase();
    if (!term) return;
    const treffer =
      keywordSuggestions.suggestions.find((k) => k.keyword.toLowerCase() === term) ??
      keywordSuggestions.suggestions[0];
    if (!treffer) {
      setKeywordSearchEmpty(true);
      return;
    }
    addKeyword(treffer);
  }

  function toggleCompInclude(titleId: string) {
    const current = compInclude[titleId] ?? true;
    setCompInclude((prev) => ({ ...prev, [titleId]: !current }));
  }

  async function handleScriptUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || uploadingScript) return;
    setUploadingScript(true);
    try {
      const base64 = await readFileAsBase64(file);
      const summary = await summarizeScript(file.name, base64);
      setScriptSummary(summary.trim() || null);
    } catch {
      logApplicationFailure("tobis.lab.scriptUpload");
    } finally {
      setUploadingScript(false);
    }
  }

  function askAboutTitles() {
    ask(
      intl.formatMessage(
        {
          id: "tobis.lab.askPrompt",
          defaultMessage:
            'Of the comparable titles you found for my pitch "{title}"{short}, which are the strongest matches, and what did they earn (DE admissions / revenue)?',
        },
        {
          title:
            pitchTitle.trim() ||
            pitchDescription.trim().slice(0, 60) ||
            intl.formatMessage({ id: "tobis.lab.untitledPitch", defaultMessage: "untitled" }),
          short: pitchShort.trim() ? ` (${pitchShort.trim()})` : "",
        },
      ),
    );
  }

  // Ein KI-Call holt sich selbst die Filmografie (inkl. Erloes/Budget) zu
  // Regie/Drehbuchautor:in/Cast, rangiert davon unabhaengig einen Themen-Pool
  // (Genre/Sprache/Keywords) und liefert direkt sowohl die ausgewaehlten
  // Vergleichstitel mit Begruendung als auch die Potenzial-Einschaetzung
  // (TobisPitchController.findComparables). Anders als die fruehere Fassung
  // ueberschreibt ein neuer Lauf NICHT mehr den vorigen - createLabSessionResult
  // legt eine eigene Zeile an (siehe lib/tobis.ts-Kommentar), der neue Lauf wird
  // nur automatisch geoeffnet, der alte bleibt in "Fruehere Ergebnisse" auffindbar.
  async function handleFindComparables() {
    if (!sessionId || findingComparables || !pitchComplete) return;
    setFindingComparables(true);
    setProgressStages([]);
    setFindError(null);
    try {
      const input: FindComparablesInput = {
        pitch: pitchText,
        genres: filters.genres.length ? [...filters.genres] : undefined,
        languages: filters.languages.length ? [...filters.languages] : undefined,
        yearFrom: filters.yearFrom.trim() || undefined,
        yearTo: filters.yearTo.trim() || undefined,
        director: director ?? undefined,
        writer: writer ?? undefined,
        cast: cast.length ? cast : undefined,
        tone: pitchHints.trim() || undefined,
        scriptSummary: scriptSummary?.trim() || undefined,
        expectedBudgetUsd: expectedBudgetUsd.trim() || undefined,
        expectedRevenueUsd: expectedRevenueUsd.trim() || undefined,
        keywordIds: keywordIds.length ? keywordIds : undefined,
        pinnedTitleIds: pinnedTitles.length ? pinnedTitles.map((t) => t.id) : undefined,
        tobisAlgorithm,
        fixedComparables: fixedActive || undefined,
      };
      // Echte Fortschritts-Events aus TobisPitchController.runFindComparables (Marvin,
      // 2026-08-07: "eine echte Progress-Leiste, welche Daten wie gesammelt werden") - kein
      // simulierter Timer, jede Zeile entspricht einem tatsaechlichen Filmografie-/Themen-Pool-/
      // Modell-Aufruf auf dem agent-service.
      const result = await findComparables(input, (event: FindComparablesProgressEvent) => {
        if (event.kind === "stage_started") {
          setProgressStages((prev) => [...prev, { stage: event.stage, label: event.label ?? event.stage, done: false }]);
        } else {
          setProgressStages((prev) => prev.map((s) => (s.stage === event.stage ? { ...s, done: true } : s)));
        }
      });
      const created = await createLabSessionResult(sessionId, {
        selected: result.selected,
        positive: result.positive,
        negative: result.negative,
        fazit: result.fazit,
        suitability: result.suitability,
        context: result.context,
      });
      setResultsList((prev) => [
        {
          id: created.id,
          createdAt: created.createdAt,
          fazit: created.fazit,
          suitability: created.suitability,
          comparableCount: created.comparableCount,
        },
        ...prev,
      ]);
      setOpenResultId(created.id);
      const next = new URLSearchParams(params);
      next.set("result", created.id);
      setParams(next, { replace: true });
      await hydrateResultDetail(created);
    } catch (err) {
      logApplicationFailure("tobis.lab.findComparables");
      setFindError(userErrorMessage(intl, err));
    } finally {
      setFindingComparables(false);
    }
  }

  // Auto-Start beim Betreten von Schritt 02 (Marvin, 2026-08-06), aber nur
  // fuer die allererste Untersuchung dieser Sitzung: sobald "Fruehere
  // Ergebnisse" etwas enthaelt, wuerde ein Auto-Start bei jedem Besuch
  // ungefragt einen weiteren Lauf anlegen. Und nur einmal je Seitenbesuch:
  // nach einem Fehlschlag ist die Bedingung wieder wahr, ein Effect ohne
  // Sperre startete dann endlos neu (siehe useAutoStartOnce). Nach
  // handleFindComparables deklariert, nicht davor - eslint (react-hooks) sieht
  // eine Funktionsdeklaration in einer Komponente sonst als "vor der
  // Deklaration benutzt".
  useAutoStartOnce(
    sessionId,
    // Ohne agent-service (tobis detached) liefe der Auto-Start nur in einen Fehler.
    labAi &&
      stepId === "result" &&
      resultsListLoaded &&
      resultsList.length === 0 &&
      !findingComparables &&
      pitchComplete,
    () => void handleFindComparables(),
  );

  if (!sessionId) {
    return (
      <LabMessage>
        <FormattedMessage id="tobis.lab.session.invalidUrl" defaultMessage="Invalid lab session URL." />
      </LabMessage>
    );
  }

  if (phase === "loading") {
    return (
      <LabMessage>
        <FormattedMessage id="tobis.lab.session.loading" defaultMessage="Loading…" />
      </LabMessage>
    );
  }

  if (phase === "notFound") {
    return (
      <LabMessage>
        <FormattedMessage id="tobis.lab.session.notFound" defaultMessage="Investigation not found." />{" "}
        <Link to="/tobis/lab" className="underline hover:text-ink">
          <FormattedMessage id="tobis.lab.session.backToLanding" defaultMessage="Back to Lab" />
        </Link>
      </LabMessage>
    );
  }

  const hint =
    phase === "notConfigured"
      ? intl.formatMessage({
          id: "tobis.notConfigured",
          defaultMessage:
            "No database configured. Set TOBIS_DATABASE_URL in the BFF environment and run the migration.",
        })
      : phase === "error"
        ? intl.formatMessage({ id: "tobis.loadError", defaultMessage: "The overview could not be loaded." })
        : undefined;

  const idx = stepIndex(stepId);
  const next = nextStepId(stepId);
  const prev = prevStepId(stepId);

  // Fuer den Fortschrittsbalken: dieselbe Zaehlung wie TobisPitchController.runFindComparables
  // tatsaechlich an Teilaufrufen macht (je eindeutiger Person + Themen-Pool + Modell-Aufruf,
  // gepinnte Titel nur wenn vorhanden) - ein Naeherungswert, kein Vertrag mit dem Server, deshalb
  // rundet die Prozentanzeige unten einfach, statt auf exakte Uebereinstimmung zu bestehen.
  const uniquePersonCount = new Set(
    [director, writer, ...cast].filter((p): p is TobisPersonRef => p !== null).map((p) => p.personId),
  ).size;
  // Im festen Modus laesst der agent-service den Themen-Pool aus.
  const expectedStageCount = uniquePersonCount + (fixedActive ? 0 : 1) + (pinnedTitles.length > 0 ? 1 : 0) + 1;

  const comparablesForValuation: ValuationComparableInput[] = resolved.map((r) => ({
    id: r.title.id,
    name: r.title.name,
    admissionsDe: r.title.admissionsDe,
    year: r.title.year,
    budgetUsd: r.title.budgetUsd,
    revenueUsd: r.title.revenueUsd,
    comparable: compInclude[r.title.id] ?? true,
    reasoning: r.reasoning,
    coverUrl: r.title.coverUrl,
    topCast: topCast(r.cast),
    directors: creditNames(r.cast, ["Director"]),
    writers: creditNames(r.cast, WRITING_JOBS),
    streamingOriginal: r.title.streamingOriginal,
  }));

  return (
    <TobisPageShell
      eyebrow={intl.formatMessage({ id: "tobis.eyebrow", defaultMessage: "Movie" })}
      title={intl.formatMessage({ id: "tobis.lab.heading", defaultMessage: "Lab" })}
      intro={intl.formatMessage({
        id: "tobis.lab.intro",
        defaultMessage:
          "Describe your pitch, then ask the assistant about the comparable titles it finds, e.g. which of these are comparable to a pitch, and what did they earn.",
      })}
    >
      {hint ? (
        <Section title={intl.formatMessage({ id: "tobis.lab.heading", defaultMessage: "Lab" })}>
          <p className="text-sm text-ink/55">{hint}</p>
        </Section>
      ) : (
        <>
          <LabStepper steps={LAB_STEPS} activeIndex={idx} onPick={setStepId} />

          <p className="mt-5 max-w-xl text-sm text-ink/55">
            {stepId === "pitch" ? (
              <FormattedMessage
                id="tobis.lab.pitchSubtitle"
                defaultMessage="Describe your pitch on the left, then derive search criteria on the right: genres, sources, and a few keywords. You can still adjust everything by hand."
              />
            ) : (
              <FormattedMessage
                id="tobis.lab.resultSubtitle"
                defaultMessage="One pass gathers the context behind your director, writer, and cast, then selects genuinely comparable titles and assesses potential."
              />
            )}
          </p>

          <div className="mt-6">
            {stepId === "pitch" ? (
              <div className="grid grid-cols-1 items-start gap-6 @lg:grid-cols-2">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2">
                    <div className="box-flat bg-page p-5 transition focus-within:ring-1 focus-within:ring-accent/40">
                      <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
                        <FormattedMessage id="tobis.lab.pitchTitleLabel" defaultMessage="Pitch title" />
                      </div>
                      <input
                        value={pitchTitle}
                        onChange={(e) => setPitchTitle(e.target.value)}
                        placeholder={intl.formatMessage({
                          id: "tobis.lab.pitchTitlePlaceholder",
                          defaultMessage: "e.g. Working title",
                        })}
                        className="w-full bg-transparent text-xl font-medium outline-none placeholder:text-ink/30"
                      />
                    </div>
                    <div className="box-flat bg-page p-5 transition focus-within:ring-1 focus-within:ring-accent/40">
                      <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
                        <FormattedMessage id="tobis.lab.pitchShortLabel" defaultMessage="Logline" />
                      </div>
                      <input
                        value={pitchShort}
                        onChange={(e) => setPitchShort(e.target.value)}
                        placeholder={intl.formatMessage({
                          id: "tobis.lab.pitchShortPlaceholder",
                          defaultMessage: "In one sentence: what, for whom?",
                        })}
                        className="w-full bg-transparent text-sm outline-none placeholder:text-ink/30"
                      />
                    </div>
                  </div>

                  <div className="box-flat bg-page p-5 transition focus-within:ring-1 focus-within:ring-accent/40">
                    <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
                      <FormattedMessage id="tobis.lab.pitchDescriptionLabel" defaultMessage="Pitch" />
                    </div>
                    <textarea
                      value={pitchDescription}
                      onChange={(e) => setPitchDescription(e.target.value)}
                      placeholder={intl.formatMessage({
                        id: "tobis.lab.pitchDescriptionPlaceholder",
                        defaultMessage: "Tone, genre, cast ideas, setting, whatever defines the comparison.",
                      })}
                      rows={5}
                      className="field-sizing-content w-full resize-none bg-transparent text-sm leading-relaxed outline-none placeholder:text-ink/30"
                    />
                    <div className="mt-2 text-right text-[10px] tracking-wider text-ink/40 tabular-nums">
                      <FormattedMessage
                        id="tobis.lab.charsCount"
                        defaultMessage="{count} characters"
                        values={{ count: pitchDescription.length }}
                      />
                    </div>
                  </div>

                  <div className="box-flat smycles-ai-shadow bg-page p-5 transition focus-within:ring-1 focus-within:ring-accent/60">
                    <div
                      className="mb-1 text-[10px] uppercase tracking-[0.25em]"
                      style={{ color: "var(--color-accent)" }}
                    >
                      <FormattedMessage id="tobis.lab.hintsLabel" defaultMessage="Tone" />
                    </div>
                    <textarea
                      value={pitchHints}
                      onChange={(e) => setPitchHints(e.target.value)}
                      placeholder={intl.formatMessage({
                        id: "tobis.lab.hintsPlaceholder",
                        defaultMessage: "Optional: mood, atmosphere, or style notes the pitch text alone might miss.",
                      })}
                      rows={2}
                      className="field-sizing-content w-full resize-none bg-transparent text-sm leading-relaxed outline-none placeholder:text-ink/30"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-[10px] tracking-[0.25em] text-ink/45">
                      <FormattedMessage id="tobis.lab.expectedCurrencyLabel" defaultMessage="Currency" />
                    </div>
                    <SegmentedControl
                      size="sm"
                      value={expectedCurrency}
                      onChange={setExpectedCurrency}
                      options={[
                        { value: "EUR", label: "€" },
                        { value: "USD", label: "$" },
                      ]}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2">
                    <div className="box-flat bg-page p-5 transition focus-within:ring-1 focus-within:ring-accent/40">
                      <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
                        <FormattedMessage id="tobis.lab.expectedBudgetLabel" defaultMessage="Budget" />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-ink/40">{expectedCurrency === "EUR" ? "€" : "$"}</span>
                        <input
                          value={expectedBudgetUsd ? intl.formatNumber(Number(expectedBudgetUsd)) : ""}
                          onChange={(e) => setExpectedBudgetUsd(e.target.value.replace(/[^0-9]/g, ""))}
                          inputMode="numeric"
                          placeholder={intl.formatMessage({
                            id: "tobis.lab.expectedBudgetPlaceholder",
                            defaultMessage: "Optional",
                          })}
                          className="w-full bg-transparent text-sm tabular-nums outline-none placeholder:text-ink/30"
                        />
                      </div>
                    </div>
                    <div className="box-flat bg-page p-5 transition focus-within:ring-1 focus-within:ring-accent/40">
                      <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
                        <FormattedMessage
                          id="tobis.lab.expectedRevenueLabel"
                          defaultMessage="Expected worldwide revenue"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-ink/40">{expectedCurrency === "EUR" ? "€" : "$"}</span>
                        <input
                          value={expectedRevenueUsd ? intl.formatNumber(Number(expectedRevenueUsd)) : ""}
                          onChange={(e) => setExpectedRevenueUsd(e.target.value.replace(/[^0-9]/g, ""))}
                          inputMode="numeric"
                          placeholder={intl.formatMessage({
                            id: "tobis.lab.expectedRevenuePlaceholder",
                            defaultMessage: "Optional",
                          })}
                          className="w-full bg-transparent text-sm tabular-nums outline-none placeholder:text-ink/30"
                        />
                      </div>
                    </div>
                  </div>

                  {labAi || scriptSummary ? (
                    <div className="box-flat bg-page p-5">
                      <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
                        <FormattedMessage id="tobis.lab.scriptLabel" defaultMessage="Script" />
                      </div>
                      {scriptSummary ? (
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-[13px] leading-relaxed text-ink/70">{scriptSummary}</p>
                          <button
                            type="button"
                            onClick={() => setScriptSummary(null)}
                            aria-label={intl.formatMessage({
                              id: "tobis.lab.scriptRemove",
                              defaultMessage: "Remove script",
                            })}
                            className="shrink-0 rounded-full p-1 text-ink/40 transition hover:bg-red-600/10 hover:text-red-600"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-ink/55 transition hover:text-ink">
                          <input
                            type="file"
                            accept="application/pdf"
                            onChange={(e) => void handleScriptUpload(e)}
                            disabled={uploadingScript}
                            className="sr-only"
                          />
                          {uploadingScript ? (
                            <>
                              <span
                                aria-hidden
                                className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
                              />
                              <FormattedMessage id="tobis.lab.aiWorking" defaultMessage="Working…" />
                            </>
                          ) : (
                            <FormattedMessage
                              id="tobis.lab.scriptUploadPrompt"
                              defaultMessage="Upload a script PDF (optional)"
                            />
                          )}
                        </label>
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="box bg-surface p-6 pt-7">
                  <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <div className="text-[11px] tracking-[0.25em] text-ink/55">
                      <FormattedMessage id="tobis.lab.criteria" defaultMessage="Criteria" />
                    </div>
                    {SHOW_PITCH_AI_GENERATE ? (
                      <Tooltip
                        placement="left"
                        content={
                          generating
                            ? intl.formatMessage({
                                id: "tobis.lab.aiWorking",
                                defaultMessage: "Working…",
                              })
                            : canGenerate
                              ? intl.formatMessage({
                                  id: "tobis.lab.aiDeriveHint",
                                  defaultMessage:
                                    "Matches words from the pitch against the real TMDB keyword catalog. You can remove any keyword afterwards.",
                                })
                              : intl.formatMessage({
                                  id: "tobis.lab.aiFillFirstHint",
                                  defaultMessage: "Fill in the pitch or search hints first.",
                                })
                        }
                      >
                        <Button
                          variant="secondary"
                          size="compact"
                          className="gap-1.5 whitespace-nowrap"
                          onClick={() => void handleGenerate()}
                          disabled={!canGenerate || generating}
                          aria-busy={generating}
                        >
                          {generating ? (
                            <>
                              <span
                                aria-hidden
                                className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
                              />
                              <FormattedMessage id="tobis.lab.aiGenerating" defaultMessage="Generating…" />
                            </>
                          ) : (
                            <FormattedMessage id="tobis.lab.generateWithAi" defaultMessage="Generate with AI" />
                          )}
                        </Button>
                      </Tooltip>
                    ) : null}
                  </div>

                  {!pitchComplete ? (
                    <div className="mb-6 border-l-2 border-accent bg-accent/5 px-4 py-3">
                      <div className="text-[10px] tracking-[0.2em] text-accent/75">
                        <FormattedMessage id="tobis.lab.requiredNote" defaultMessage="Required" />
                      </div>
                      <div className="mt-1 field-value max-w-5xl text-ink">
                        <FormattedMessage
                          id="tobis.lab.requiredHint"
                          defaultMessage="Before comparing, fill in: {missing}"
                          values={{ missing: missingRequired.join(", ") }}
                        />
                      </div>
                    </div>
                  ) : null}

                  <div className="mb-6 border-l-2 border-accent bg-accent/5 px-4 py-3">
                    <div className="text-[10px] tracking-[0.2em] text-accent/75">
                      <FormattedMessage id="tobis.lab.hint" defaultMessage="Note" />
                    </div>
                    <div className="mt-1 field-value max-w-5xl text-ink">
                      <FormattedMessage
                        id="tobis.lab.hintText"
                        defaultMessage="Keywords only rank the theme pool with TMDB coverage; genres/languages narrow it further. Director, writer, and cast add their own filmography as separate context."
                      />
                    </div>
                  </div>

                  <div className="mb-1.5 text-[10px] tracking-[0.2em] text-ink/45">
                    <FormattedMessage id="tobis.lab.keywordsLabel" defaultMessage="Keywords" />
                  </div>
                  <input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      void addKeywordTerm();
                    }}
                    placeholder={intl.formatMessage({
                      id: "tobis.lab.keywordAddPlaceholder",
                      defaultMessage: "Add a keyword, Enter to confirm",
                    })}
                    className="mb-2 w-full rounded-md bg-page px-3 py-1.5 text-[13px] outline-none ring-1 ring-ink/10 focus-visible:ring-ink/30"
                  />

                  {/* Vorschlaege direkt unter dem Feld, einzeln anklickbar. Die
                      Trefferzahl steht mit dran: sie sagt, ob ein Keyword im
                      Bestand ueberhaupt Substanz hat - "drama" traegt Tausende
                      Titel, "drama class" eine Handvoll. */}
                  {keywordSuggestions.suggestions.length > 0 ? (
                    <div className="mb-4 max-h-56 overflow-auto rounded-md bg-page ring-1 ring-ink/10">
                      {keywordSuggestions.suggestions.map((k) => {
                        const gewaehlt = keywordMatches.some((m) => m.keywordId === k.keywordId);
                        return (
                          <button
                            key={k.keywordId}
                            type="button"
                            disabled={gewaehlt}
                            onClick={() => addKeyword(k)}
                            className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-[13px] text-ink/80 transition hover:bg-ink/[0.06] disabled:cursor-default disabled:text-ink/35 disabled:hover:bg-transparent"
                          >
                            <span className="truncate">{k.keyword}</span>
                            <span className="shrink-0 text-[11px] tabular-nums text-ink/40">
                              {gewaehlt ? (
                                <FormattedMessage id="tobis.lab.keywordAlreadyAdded" defaultMessage="added" />
                              ) : (
                                intl.formatNumber(k.titleCount)
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : keywordSuggestions.loading ? (
                    <div className="mb-4 text-[12px] text-ink/45">
                      <FormattedMessage id="tobis.filter.toneLoading" defaultMessage="Searching…" />
                    </div>
                  ) : null}

                  {keywordMatches.length > 0 ? (
                    <div className="mb-6 flex flex-wrap gap-1.5">
                      {keywordMatches.map((k) => (
                        <span
                          key={k.keywordId}
                          className="inline-flex items-center gap-1.5 rounded-full bg-ink/[0.06] px-2.5 py-1 text-[11px] text-ink/70"
                        >
                          {k.keyword}
                          <button
                            type="button"
                            onClick={() => removeKeyword(k.keywordId)}
                            className="text-ink/40 transition hover:text-ink"
                            aria-label={intl.formatMessage(
                              { id: "tobis.lab.removeKeyword", defaultMessage: "Remove {keyword}" },
                              { keyword: k.keyword },
                            )}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {keywordSearchEmpty ? (
                    <div className="mb-6 text-[12px] text-ink/45">
                      <FormattedMessage
                        id="tobis.lab.aiNoMatches"
                        defaultMessage="No TMDB keywords matched this text - try rephrasing with more common genre/theme words."
                      />
                    </div>
                  ) : null}

                  {pinnedTitles.length > 0 ? (
                    <div className="mb-6">
                      <div className="mb-1.5 text-[10px] tracking-[0.2em] text-accent/75">
                        <FormattedMessage id="tobis.lab.pinnedFromText" defaultMessage="Named in your pitch" />
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {pinnedTitles.map((t) => (
                          <span
                            key={t.id}
                            className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-2.5 py-1 text-[11px] text-ink/70"
                          >
                            {t.name ?? t.id}
                            <button
                              type="button"
                              onClick={() => removePinnedTitle(t.id)}
                              className="text-ink/40 transition hover:text-ink"
                              aria-label={intl.formatMessage(
                                { id: "tobis.lab.pinnedRemove", defaultMessage: "Unpin {name}" },
                                { name: t.name ?? t.id },
                              )}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      {labAi ? (
                        <div className="mt-3 flex items-center gap-2">
                          <Switch
                            checked={fixedComparables}
                            onChange={setFixedComparables}
                            ariaLabel={intl.formatMessage({
                              id: "tobis.lab.fixedComparables.ariaLabel",
                              defaultMessage: "Use only these comparables",
                            })}
                          />
                          <span className="text-[13px] text-ink/70">
                            <FormattedMessage
                              id="tobis.lab.fixedComparables.label"
                              defaultMessage="Use only these comparables"
                            />
                          </span>
                          <InfoTooltip
                            ariaLabel={intl.formatMessage({
                              id: "tobis.lab.fixedComparables.infoAriaLabel",
                              defaultMessage: "What \"Use only these comparables\" does",
                            })}
                          >
                            <FormattedMessage
                              id="tobis.lab.fixedComparables.info"
                              defaultMessage="The analysis assesses exactly the pinned titles: no theme pool, no similar titles, no extra titles from the cast's filmographies. The filmographies still inform how the cast's box-office draw is judged."
                            />
                          </InfoTooltip>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="mb-6 space-y-4">
                    <TobisPersonPicker
                      label={intl.formatMessage({ id: "tobis.lab.directorLabel", defaultMessage: "Director" })}
                      placeholder={intl.formatMessage({
                        id: "tobis.lab.directorPlaceholder",
                        defaultMessage: "Search by name",
                      })}
                      value={director ? [director] : []}
                      onChange={(next) => setDirector(next[0] ?? null)}
                      max={1}
                    />
                    <TobisPersonPicker
                      label={intl.formatMessage({ id: "tobis.lab.writerLabel", defaultMessage: "Screenwriter" })}
                      placeholder={intl.formatMessage({
                        id: "tobis.lab.writerPlaceholder",
                        defaultMessage: "Search by name",
                      })}
                      value={writer ? [writer] : []}
                      onChange={(next) => setWriter(next[0] ?? null)}
                      max={1}
                    />
                    <TobisPersonPicker
                      label={intl.formatMessage({ id: "tobis.lab.castLabel", defaultMessage: "Cast" })}
                      placeholder={intl.formatMessage({
                        id: "tobis.lab.castPlaceholder",
                        defaultMessage: "Search by name",
                      })}
                      value={cast}
                      onChange={setCast}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2">
                    <div>
                      <div className="text-[10px] tracking-[0.15em] text-ink/45">
                        <FormattedMessage id="tobis.filter.genres" defaultMessage="Genres" />
                      </div>
                      <div className="mt-1">
                        <MultiSelect
                          options={(options?.genres ?? []).map((v) => ({ value: v, label: v }))}
                          selected={filters.genres}
                          onChange={(next) => setFilters({ ...filters, genres: next })}
                          placeholder={intl.formatMessage({ id: "tobis.filter.genres", defaultMessage: "Genres" })}
                          searchable
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.15em] text-ink/45">
                        <FormattedMessage id="tobis.filter.languages" defaultMessage="Languages" />
                      </div>
                      <div className="mt-1">
                        <MultiSelect
                          options={(options?.languages ?? []).map((v) => ({ value: v, label: v }))}
                          selected={filters.languages}
                          onChange={(next) => setFilters({ ...filters, languages: next })}
                          placeholder={intl.formatMessage({
                            id: "tobis.filter.languages",
                            defaultMessage: "Languages",
                          })}
                          searchable
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className={`mt-4 grid grid-cols-2 gap-4 @sm:w-1/2 ${tobisAlgorithm ? "opacity-40" : ""}`}>
                    <div>
                      <div className="text-[10px] tracking-[0.15em] text-ink/45">
                        <FormattedMessage id="tobis.filter.yearFrom" defaultMessage="Year from" />
                      </div>
                      <input
                        type="number"
                        value={filters.yearFrom}
                        onChange={(e) => setFilters({ ...filters, yearFrom: e.target.value })}
                        placeholder="1990"
                        disabled={tobisAlgorithm}
                        className="mt-1 w-full rounded-md bg-page px-3 py-1.5 text-[13px] outline-none ring-1 ring-ink/10 focus-visible:ring-ink/30 disabled:cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.15em] text-ink/45">
                        <FormattedMessage id="tobis.filter.yearTo" defaultMessage="Year to" />
                      </div>
                      <input
                        type="number"
                        value={filters.yearTo}
                        onChange={(e) => setFilters({ ...filters, yearTo: e.target.value })}
                        placeholder="2026"
                        disabled={tobisAlgorithm}
                        className="mt-1 w-full rounded-md bg-page px-3 py-1.5 text-[13px] outline-none ring-1 ring-ink/10 focus-visible:ring-ink/30 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {SHOW_TOBIS_ALGORITHM_TOGGLE ? (
                    <div className="mt-4 flex items-center gap-2">
                      <Switch
                        checked={tobisAlgorithm}
                        onChange={setTobisAlgorithm}
                        ariaLabel={intl.formatMessage({
                          id: "tobis.lab.tobisAlgorithm.ariaLabel",
                          defaultMessage: "TOBIS Algorithmus",
                        })}
                      />
                      <span className="text-[13px] text-ink/70">
                        <FormattedMessage id="tobis.lab.tobisAlgorithm.label" defaultMessage="TOBIS Algorithmus" />
                      </span>
                      <InfoTooltip
                        ariaLabel={intl.formatMessage({
                          id: "tobis.lab.tobisAlgorithm.infoAriaLabel",
                          defaultMessage: "What the TOBIS Algorithmus does",
                        })}
                      >
                        <FormattedMessage
                          id="tobis.lab.tobisAlgorithm.info"
                          defaultMessage="Applies Movie's fixed comparison rules instead of the filters above: only titles from the last 12 years with a German cinema release or a streaming original, and 5 to 15 comparables instead of up to 8. Overrides year from/to while active."
                        />
                      </InfoTooltip>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <Section
                title={intl.formatMessage({ id: "tobis.lab.resultHeading", defaultMessage: "Comparable titles" })}
                right={
                  <div className="flex items-center gap-2 print:hidden">
                    {resolved.length > 0 ? (
                      <Button variant="secondary" size="compact" onClick={() => window.print()}>
                        <FormattedMessage id="tobis.lab.exportPdf" defaultMessage="Export as PDF" />
                      </Button>
                    ) : null}
                    {!findingComparables ? (
                      <Tooltip
                        placement="left"
                        content={
                          pitchComplete
                            ? intl.formatMessage({
                                id: "tobis.lab.findComparablesHint",
                                defaultMessage:
                                  "One model call gathers filmography context for director/writer/cast, ranks a theme pool, and assesses potential.",
                              })
                            : intl.formatMessage(
                                { id: "tobis.lab.requiredHint", defaultMessage: "Before comparing, fill in: {missing}" },
                                { missing: missingRequired.join(", ") },
                              )
                        }
                      >
                        <Button
                          variant="secondary"
                          size="compact"
                          onClick={() => void handleFindComparables()}
                          disabled={!pitchComplete}
                        >
                          {resultsList.length > 0 ? (
                            <FormattedMessage id="tobis.lab.regenerate" defaultMessage="Find again" />
                          ) : (
                            <FormattedMessage id="tobis.lab.compare" defaultMessage="Find comparables" />
                          )}
                        </Button>
                      </Tooltip>
                    ) : null}
                  </div>
                }
                empty={
                  phase === "ready" &&
                  pitchComplete &&
                  resultsListLoaded &&
                  resultsList.length === 0 &&
                  !findingComparables &&
                  resolved.length === 0
                }
                emptyLabel={intl.formatMessage({
                  id: "tobis.lab.empty",
                  defaultMessage: "No comparable titles found.",
                })}
                emptyHint={intl.formatMessage({
                  id: "tobis.lab.emptyHint",
                  defaultMessage: "Add more detail to the pitch, or try again.",
                })}
              >
                {/* Nur beim Druck sichtbar (window.print(), siehe Export-Button oben) - der
                    Bildschirm zeigt Pitch-Titel/Logline/Text schon im Pitch-Schritt, ein
                    gedrucktes Ergebnis-Blatt ohne diesen Kontext waere aber nicht
                    eigenstaendig lesbar. */}
                <div className="hidden print:mb-6 print:block">
                  <div className="text-[10px] tracking-[0.25em] text-ink/55">
                    <FormattedMessage id="tobis.lab.exportPitchHeading" defaultMessage="Pitch" />
                  </div>
                  <div className="text-lg font-medium">
                    {pitchTitle.trim() || (
                      <FormattedMessage id="tobis.lab.untitledPitch" defaultMessage="untitled" />
                    )}
                  </div>
                  {pitchShort.trim() ? <p className="text-sm text-ink/65">{pitchShort}</p> : null}
                  {pitchDescription.trim() ? (
                    <p className="mt-2 whitespace-pre-wrap text-sm text-ink/80">{pitchDescription}</p>
                  ) : null}
                </div>

                {findError ? (
                  <p role="alert" className="mb-3 text-[13px] text-ink/70">
                    {findError}
                  </p>
                ) : null}
                {findingComparables ? (
                  <div className="space-y-3 py-8">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
                      <div
                        className="h-full rounded-full bg-accent transition-[width] duration-500"
                        style={{
                          width: `${
                            expectedStageCount > 0
                              ? Math.round((progressStages.filter((s) => s.done).length / expectedStageCount) * 100)
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    <ul className="space-y-1 text-[12px] text-ink/55">
                      {progressStages.map((s) => (
                        <li key={s.stage} className="flex items-center gap-2">
                          {s.done ? (
                            <span aria-hidden className="text-accent">
                              ✓
                            </span>
                          ) : (
                            <span
                              aria-hidden
                              className="inline-block h-2.5 w-2.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
                            />
                          )}
                          {s.label}
                        </li>
                      ))}
                      {progressStages.length === 0 ? (
                        <li className="flex items-center gap-2">
                          <span
                            aria-hidden
                            className="inline-block h-2.5 w-2.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
                          />
                          <FormattedMessage id="tobis.lab.findingComparables" defaultMessage="Starting…" />
                        </li>
                      ) : null}
                    </ul>
                  </div>
                ) : loadingResultDetail ? (
                  <div className="flex items-center gap-2 py-10 text-sm text-ink/55">
                    <span
                      aria-hidden
                      className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                    />
                    <FormattedMessage id="tobis.lab.loadingResult" defaultMessage="Loading…" />
                  </div>
                ) : openResultId !== null ? (
                  // A genuinely empty selection can show the model assessment.
                  // Failed detail loads instead show a retry action; never calculate
                  // a valuation from an accidentally incomplete comparison set.
                  <div className="space-y-4">
                    {failedComparableIds.length > 0 ? (
                      <div role="alert" className="space-y-3 text-sm text-ink/70">
                        <p>
                          <FormattedMessage
                            id="tobis.lab.comparablesLoadError"
                            defaultMessage="Could not load {count} selected comparable films. Your saved result is intact. Reload the film data without starting a new AI search."
                            values={{ count: failedComparableIds.length }}
                          />
                        </p>
                        <Button variant="secondary" size="compact" onClick={() => void openResult(openResultId)}>
                          <FormattedMessage id="tobis.lab.retryComparables" defaultMessage="Reload film data" />
                        </Button>
                      </div>
                    ) : <TobisValuationPanel
                      comparables={comparablesForValuation}
                      include={compInclude}
                      onToggleInclude={toggleCompInclude}
                      manual={valuationManual}
                      onManualChange={setValuationManual}
                      assessment={assessment}
                      expectedBudgetUsd={expectedBudgetUsd || null}
                      expectedRevenueUsd={expectedRevenueUsd || null}
                    />}
                    {labAi ? (
                      <div className="flex justify-end print:hidden">
                        <Button variant="secondary" size="compact" onClick={askAboutTitles}>
                          <FormattedMessage id="tobis.lab.askAssistant" defaultMessage="Ask the assistant" />
                        </Button>
                      </div>
                    ) : null}
                    {context ? (
                      <Disclosure
                        className="print:hidden"
                        title={intl.formatMessage({
                          id: "tobis.lab.contextHeading",
                          defaultMessage: "Context sent to the AI",
                        })}
                      >
                        {/* Kein eigenes max-h/overflow-auto mehr (Fund 07.08.: bei langem Kontext
                            stand hier eine zweite Scrollbar direkt neben der Seiten-Scrollbar) -
                            fliesst mit der Seite, die main-Scrollbar von AppShell reicht. */}
                        <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-ink/70">
                          {context}
                        </pre>
                      </Disclosure>
                    ) : null}
                  </div>
                ) : !pitchComplete ? (
                  <p className="py-10 text-sm text-ink/55">
                    <FormattedMessage
                      id="tobis.lab.requiredHint"
                      defaultMessage="Before comparing, fill in: {missing}"
                      values={{ missing: missingRequired.join(", ") }}
                    />
                  </p>
                ) : null}

                {/* Uebersicht wie MatchResult (ResultsHistory) - jeder Lauf eine
                    Zeile, Klick oeffnet ihn oben, kein Ueberschreiben mehr
                    (Marvin, 2026-08-07). Immer sichtbar, sobald mehr als der
                    gerade offene Lauf existiert - bei genau einem waere die
                    Liste nur eine Wiederholung von dem, was schon oben steht. */}
                {resultsList.length > 1 ? (
                  <Disclosure
                    className="mt-4"
                    title={intl.formatMessage({
                      id: "tobis.lab.resultsHistoryHeading",
                      defaultMessage: "Earlier results",
                    })}
                    count={resultsList.length}
                  >
                    <ul className="space-y-1.5">
                      {resultsList.map((r) => {
                        const isOpen = r.id === openResultId;
                        return (
                          <li key={r.id} className="flex items-start gap-2">
                            <button
                              type="button"
                              onClick={() => void openResult(r.id)}
                              disabled={isOpen}
                              className={`min-w-0 flex-1 rounded px-2 py-1.5 text-left text-[12px] transition ${
                                isOpen ? "bg-ink/[0.06]" : "hover:bg-ink/[0.04]"
                              }`}
                            >
                              <span className="flex items-baseline justify-between gap-3">
                                <span className="text-ink/70">
                                  {intl.formatDate(r.createdAt, { dateStyle: "medium", timeStyle: "short" })}
                                  {isOpen ? (
                                    <span className="ml-2 text-[10px] tracking-wide text-accent/75">
                                      <FormattedMessage id="tobis.lab.resultsHistoryOpen" defaultMessage="open" />
                                    </span>
                                  ) : null}
                                </span>
                                {r.suitability !== null ? (
                                  <span className="shrink-0 tabular-nums text-ink/55">{r.suitability}%</span>
                                ) : null}
                              </span>
                              {r.fazit ? <p className="mt-0.5 line-clamp-1 text-ink/45">{r.fazit}</p> : null}
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirmId(r.id)}
                              aria-label={intl.formatMessage({
                                id: "tobis.lab.resultsHistoryDelete",
                                defaultMessage: "Delete this result",
                              })}
                              className="shrink-0 rounded-full p-1.5 text-ink/40 transition hover:bg-red-600/10 hover:text-red-600"
                            >
                              ×
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </Disclosure>
                ) : null}
              </Section>
            )}
          </div>

          <ConfirmDialog
            open={deleteConfirmId !== null}
            title={intl.formatMessage({
              id: "tobis.lab.resultsHistoryDeleteConfirmTitle",
              defaultMessage: "Delete this result?",
            })}
            message={intl.formatMessage({
              id: "tobis.lab.resultsHistoryDeleteConfirmMessage",
              defaultMessage: "This cannot be undone.",
            })}
            onConfirm={() => {
              if (deleteConfirmId) void handleDeleteResult(deleteConfirmId);
            }}
            onCancel={() => setDeleteConfirmId(null)}
          />

          <div className="pointer-events-none sticky bottom-4 z-10 mt-10 flex items-center justify-between sm:bottom-8 print:hidden">
            {prev ? (
              <span className="pointer-events-auto rounded-full bg-page shadow-xl">
                <Button variant="secondary" onClick={() => setStepId(prev)} className="tracking-[0.2em]">
                  ← {LAB_STEPS[idx - 1]!.n}{" "}
                  <FormattedMessage
                    id={LAB_STEPS[idx - 1]!.labelId}
                    defaultMessage={LAB_STEPS[idx - 1]!.defaultLabel}
                  />
                </Button>
              </span>
            ) : (
              <span />
            )}
            {next ? (
              (() => {
                const nextButton = (
                  <Button
                    variant="primary"
                    onClick={() => setStepId(next)}
                    disabled={!pitchComplete}
                    className="tracking-[0.2em] shadow-xl"
                  >
                    {LAB_STEPS[idx + 1]!.n}{" "}
                    <FormattedMessage
                      id={LAB_STEPS[idx + 1]!.labelId}
                      defaultMessage={LAB_STEPS[idx + 1]!.defaultLabel}
                    />{" "}
                    →
                  </Button>
                );
                return pitchComplete ? (
                  <span className="pointer-events-auto">{nextButton}</span>
                ) : (
                  <Tooltip
                    placement="top"
                    content={intl.formatMessage(
                      { id: "tobis.lab.requiredHint", defaultMessage: "Before comparing, fill in: {missing}" },
                      { missing: missingRequired.join(", ") },
                    )}
                  >
                    <span className="pointer-events-auto">{nextButton}</span>
                  </Tooltip>
                );
              })()
            ) : (
              <span />
            )}
          </div>
        </>
      )}
    </TobisPageShell>
  );
}

function LabMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <div className="bg-page box-flat p-6 text-sm text-ink/55">{children}</div>
    </div>
  );
}

// Kopiert/angepasst aus MatchWorkflow.tsx's privater Stepper-Funktion (dort
// selbst kein dokumentiertes Primitive, siehe Datei-Kommentar oben) - gleiche
// Optik (Zahl + Linie + grosses Serif-Label auf dem aktiven Schritt), auf
// LabStepId statt Match-StepId.
function LabStepper({
  steps,
  activeIndex,
  onPick,
}: {
  steps: typeof LAB_STEPS;
  activeIndex: number;
  onPick: (id: LabStepId) => void;
}) {
  const last = steps.length - 1;
  return (
    <div
      className="mt-5 grid print:hidden"
      style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}
    >
      {steps.map((s, i) => {
        const state = i < activeIndex ? "done" : i === activeIndex ? "active" : "todo";
        const faintLine = "rgb(from currentColor r g b / 0.15)";
        const numColor =
          state === "active"
            ? "var(--color-accent)"
            : state === "done"
              ? "currentColor"
              : "rgb(from currentColor r g b / 0.35)";
        const align = i === 0 ? "items-start" : i === last ? "items-end" : "items-center";
        const labelClass =
          state === "active"
            ? "mt-3 font-serif text-3xl leading-none tracking-tight"
            : "mt-3 text-[10px] tracking-[0.25em]";
        const labelColor = state === "todo" ? "rgb(from currentColor r g b / 0.4)" : "currentColor";
        return (
          <button key={s.id} onClick={() => onPick(s.id)} className={`flex flex-col ${align}`}>
            <div className="flex w-full items-center">
              {i !== 0 && (
                <div
                  className="h-px flex-1"
                  style={{ background: activeIndex >= i ? "currentColor" : faintLine }}
                />
              )}
              <span
                className={`text-[11px] font-medium tracking-wider ${i !== 0 ? "ml-3" : ""} ${
                  i !== last ? "mr-3" : ""
                }`}
                style={{ color: numColor }}
              >
                {s.n}
              </span>
              {i !== last && (
                <div
                  className="h-px flex-1"
                  style={{ background: activeIndex > i ? "currentColor" : faintLine }}
                />
              )}
            </div>
            <span className={labelClass} style={{ color: labelColor }}>
              <FormattedMessage id={s.labelId} defaultMessage={s.defaultLabel} />
            </span>
          </button>
        );
      })}
    </div>
  );
}
